import { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Button, TextInput } from '@c2fo/liquidity';
import { BoltIcon } from '@c2fo/liquidity/icons';
import { useLocationsState } from '../../contexts';
import { updateLocation } from '../../api/Locations';
import { ConnectivityState } from '../../api/shared';
import { useErrorState } from '../ErrorBanner';
import { useApiContext } from '../../contexts/FileDeliveryContext';
import { StorageTypeChip } from '../StorageTypeChip';
import { CredentialSelector } from '../CredentialSelector';
import { BackLink } from '../BackLink';
import { Card } from '../Card';
import { CardFooter } from '../CardFooter';
import { FormField } from '../FormField';
import { LoadingText } from '../LoadingText';
import { NotFoundCard } from '../NotFoundCard';
import { PageContainer } from '../PageContainer';
import { formatDuration, getConnectivityStatusLabel, getConnectivityStatusColor } from '../../utils';
import type { TestConnectivityResponse } from '../../api/Locations';

export const LocationForm: React.FC = () => {
  const { locationUid } = useParams<{ locationUid: string }>();
  const navigate = useNavigate();
  const { addError } = useErrorState();
  const apiContext = useApiContext();
  const {
    currentLocation: location,
    isLoadingLocation: isLoading,
    fetchLocation,
    clearCurrentLocation,
    testLocation,
  } = useLocationsState();
  const [displayName, setDisplayName] = useState('');
  const [description, setDescription] = useState('');
  const [credential, setCredential] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [isTesting, setIsTesting] = useState(false);
  const [testResult, setTestResult] = useState<TestConnectivityResponse | null>(null);

  useEffect(() => {
    if (locationUid) {
      // Construct full path: locations/{locationUid}
      fetchLocation(`locations/${locationUid}`);
    }

    return () => {
      clearCurrentLocation();
    };
  }, [locationUid, fetchLocation, clearCurrentLocation]);

  useEffect(() => {
    if (location) {
      setDisplayName(location.displayName);
      setDescription(location.description ?? '');
      setCredential(typeof location.credential === 'string' ? location.credential : (location.credential?.name ?? ''));
    }
  }, [location]);

  const handleTestConnectivity = async () => {
    if (!location) return;
    setIsTesting(true);
    setTestResult(null);
    try {
      const result = await testLocation(location.name);
      setTestResult(result);
    } catch {
      setTestResult({
        connectivityState: ConnectivityState.CONNECTIVITY_STATE_DISCONNECTED,
        message: 'Test failed',
      });
    } finally {
      setIsTesting(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!location) return;

    setIsSaving(true);
    try {
      await updateLocation(apiContext, {
        location: {
          name: location.name,
          displayName,
          description,
          credential,
        },
        updateMask: ['display_name', 'description', 'credential'],
      });
      navigate(`/file-delivery/${location.name}`);
    } catch {
      addError({ id: 'update-location-error', message: 'Failed to update location', type: 'error' });
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return <LoadingText />;
  }

  if (!location) {
    return (
      <PageContainer>
        <BackLink to="/file-delivery/locations" label="Back to Locations" />
        <NotFoundCard message="Location not found or could not be loaded." />
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <BackLink to={`/file-delivery/${location.name}`} label="Back to Location Details" />

      <form onSubmit={handleSubmit}>
        <Card>
          <div className="px-6 pt-4 pb-4 flex items-start justify-between">
            <div>
              <StorageTypeChip storageType={location.storageType} />
              <h2 className="text-lg font-medium mt-3 text-gray-900">Editing {location.displayName}</h2>
            </div>
            <Button
              variant="secondary"
              className="rounded-md"
              size="sm"
              type="button"
              onClick={handleTestConnectivity}
              disabled={isTesting}
            >
              <span className="flex items-center gap-1">
                <BoltIcon />
                {isTesting ? 'Testing...' : 'Test Connectivity'}
              </span>
            </Button>
          </div>
          {testResult && (
            <div
              className={`px-6 py-3 border-b ${getConnectivityStatusColor(testResult.connectivityState).bg} ${getConnectivityStatusColor(testResult.connectivityState).border}`}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <span
                    className={`text-sm font-medium ${getConnectivityStatusColor(testResult.connectivityState).text}`}
                  >
                    {testResult.connectivityState === ConnectivityState.CONNECTIVITY_STATE_CONNECTED ? '✓' : '✗'}{' '}
                    {getConnectivityStatusLabel(testResult.connectivityState)}
                  </span>
                  {testResult.message && (
                    <span className={`text-sm ${getConnectivityStatusColor(testResult.connectivityState).text}`}>
                      — {testResult.message}
                    </span>
                  )}
                </div>
                {testResult.latency && (
                  <span className={`text-sm ${getConnectivityStatusColor(testResult.connectivityState).text}`}>
                    {formatDuration(testResult.latency)}
                  </span>
                )}
              </div>
            </div>
          )}
          <div className="px-6 pb-6 space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <FormField label="Display Name" required htmlFor="displayName">
                <TextInput
                  id="displayName"
                  className="w-full"
                  value={displayName}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setDisplayName(e.target.value)}
                  placeholder="Enter display name"
                  required
                  size="sm"
                />
              </FormField>

              <FormField label="Credential" htmlFor="credential">
                <CredentialSelector
                  value={credential}
                  onChange={setCredential}
                  onError={(message) => addError({ id: 'create-credential-error', message, type: 'error' })}
                />
              </FormField>
            </div>

            <FormField label="Description" htmlFor="description">
              <TextInput
                id="description"
                className="w-full"
                value={description}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setDescription(e.target.value)}
                placeholder="Enter description (optional)"
                size="sm"
              />
            </FormField>

            <FormField label="URI (read-only)" htmlFor="uri">
              <TextInput
                id="uri"
                value={location.uri}
                disabled
                size="sm"
                className="w-full bg-gray-100 text-gray-900 font-mono"
              />
              <p className="mt-1 text-xs text-gray-500">URI cannot be changed after creation.</p>
            </FormField>
          </div>
          <CardFooter>
            <Link to={`/file-delivery/${location.name}`}>
              <Button variant="tertiary" className="rounded-md" type="button">
                Cancel
              </Button>
            </Link>
            <Button variant="primary" className="rounded-md" type="submit" disabled={isSaving || !displayName}>
              {isSaving ? 'Saving...' : 'Save Changes'}
            </Button>
          </CardFooter>
        </Card>
      </form>
    </PageContainer>
  );
};
